import React from 'react'

import useTranslation from '@/Hooks/UseTranslation'

import './RelationTypeSelector.scss'

export type RelationTypeOption = {
  value: string
  label: string
  description: string
  code: string
}

type Props = {
  value: string
  options: RelationTypeOption[]
  originName?: string | null
  destinationName?: string | null
  disabled?: boolean
  onChange: (value: string) => void
}

type RelationDescriptionProps = {
  description: string
  originName?: React.ReactNode
  destinationName?: React.ReactNode
}

export const RelationDescription = ({ description, originName, destinationName }: RelationDescriptionProps) => {

  const { t } = useTranslation()

  return (
    <>
      {description.split(/({{origin}}|{{destination}})/g).map((part, index) => {

        if (part === '{{origin}}') {
          return originName
            ? <strong key={`${part}-${index}`}>{originName}</strong>
            : <span key={`${part}-${index}`}>{t('RELATIONS_CREATOR:OriginReference')}</span>
        }

        if (part === '{{destination}}') {
          return destinationName
            ? <strong key={`${part}-${index}`}>{destinationName}</strong>
            : <span key={`${part}-${index}`}>{t('RELATIONS_CREATOR:DestinationReference')}</span>
        }

        return part
      })}
    </>
  )
}

const RelationTypeGraphic = ({ type }: { type: string }) => {

  const sourceX = type.startsWith('start_to_') ? 16 : 96
  const targetX = type.endsWith('_to_start') ? 16 : 96
  const bendX = sourceX === 16 ? 6 : 106
  const connectorPath = `M ${sourceX} 12 H ${bendX} V 36 H ${targetX}`

  return (
    <svg className="relation-type-graphic" viewBox="0 0 112 48" aria-hidden="true">
      <rect className="relation-type-graphic-bar" x="16" y="7" width="80" height="10" rx="2" />
      <rect className="relation-type-graphic-bar destination" x="16" y="31" width="80" height="10" rx="2" />
      <path className="relation-type-graphic-line" d={connectorPath} />
      <circle className="relation-type-graphic-origin" cx={sourceX} cy="12" r="4" />
      <circle className="relation-type-graphic-destination" cx={targetX} cy="36" r="4" />
    </svg>
  )
}

const RelationTypeSelector = ({ value, options, originName, destinationName, disabled, onChange }: Props) => {

  const { t } = useTranslation()

  return (
    <div className="relation-type-options" role="radiogroup" aria-label={t('RELATIONS_CREATOR:Behavior')}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={`relation-type-option${value === option.value ? ' active' : ''}`}
          onClick={() => onChange(option.value)}
          role="radio"
          aria-checked={value === option.value}
          disabled={disabled}
        >
          <RelationTypeGraphic type={option.value} />
          <span className="relation-type-option-copy">
            <strong>{option.label}</strong>
            <small>
              <RelationDescription
                description={option.description}
                originName={originName}
                destinationName={destinationName}
              />
            </small>
          </span>
        </button>
      ))}
    </div>
  )
}

export default RelationTypeSelector
